// RoboTeam · entitlement / gating del área de miembros (SCAFFOLD · 2026-06-25)
// ---------------------------------------------------------------------------
// El área de miembros RoboTeam (páginas /roboteam) es PRIVADA, post-compra. El
// acceso se gobierna por el entitlement RoboTeam del alumno, que reutiliza el
// mismo mecanismo que el resto del campus: los `course_slug` matriculados que
// devuelve `fetchMyCourses(auth)` → `enrolled[].slug`.
//
// Mapeo etiqueta de compra (GHL) → entitlement (course_slug):
//   `RB - Roboteam-basic`  →  course_slug = 'roboteam-basic'   → ve RoboTeam Basic
//   `RB - Roboteam-pro`    →  course_slug = 'roboteam-pro'     → ve RoboTeam Pro
//
// Regla dura (del doc 04-AREA-MIEMBROS-campus.md §2):
//   - entitlement 'roboteam-pro'   → página RoboTeam Pro.
//   - entitlement 'roboteam-basic' → página RoboTeam Basic.
//   - Pro NO ve Basic: es su propio plan. Si por lo que sea un alumno tuviera
//     ambos entitlements, gana Pro (rango superior).
//   - sin entitlement RoboTeam → no ve el apartado (la página muestra un CTA a
//     la venta pública en lugar de contenido privado).
//
// ⚠️ TODO (fase 2 · pendiente confirmar con Carlos + Andrés):
//   El slug 'roboteam' YA existe en el campus (Cursos.tsx) como la TARJETA de
//   curso/venta de RoboTeam, NO como el entitlement del miembro por plan. La
//   sync GHL→campus que traduce las etiquetas `RB - Roboteam-basic|pro` a los
//   course_slug 'roboteam-basic'|'roboteam-pro' AÚN NO ESTÁ IMPLEMENTADA
//   (ver 03-CHECKOUTS-y-tecnico-moises.md §6). Usamos aquí los slugs más
//   plausibles ('roboteam-basic'/'roboteam-pro') como contrato de gating; al
//   conectar la sync hay que (a) confirmar/ajustar estos slugs y (b) sembrar
//   los entitlements en `campus`/`licensing`. Mientras tanto, sin esos slugs
//   matriculados el área queda en estado CTA (fail-closed · no filtra contenido).

/** Slugs de entitlement del área de miembros RoboTeam, por plan. */
export const ROBOTEAM_BASIC_SLUG = 'roboteam-basic';
export const ROBOTEAM_PRO_SLUG = 'roboteam-pro';

/** Plan RoboTeam efectivo del alumno · null si no es miembro RoboTeam. */
export type RoboTeamTier = 'basic' | 'pro';

/**
 * Resuelve el plan RoboTeam del alumno a partir de los slugs de cursos/
 * entitlements matriculados (los de `fetchMyCourses(auth).enrolled[].slug`).
 *
 * Pro gana sobre Basic (rango superior · Pro NO degrada a Basic). Devuelve
 * `null` si el alumno no tiene ningún entitlement RoboTeam → la página debe
 * mostrar el CTA de venta, nunca contenido privado (deny-by-default). La
 * decisión de seguridad REAL es server-side; esto solo decide qué se MUESTRA.
 */
export function resolveRoboTeamTier(
  enrolledSlugs: ReadonlySet<string> | readonly string[],
): RoboTeamTier | null {
  const has = (slug: string): boolean =>
    enrolledSlugs instanceof Set
      ? enrolledSlugs.has(slug)
      : (enrolledSlugs as readonly string[]).includes(slug);

  if (has(ROBOTEAM_PRO_SLUG)) return 'pro';
  if (has(ROBOTEAM_BASIC_SLUG)) return 'basic';
  // slug genérico 'roboteam' → pro (todos los compradores actuales son pro)
  if (has('roboteam')) return 'pro';
  return null;
}

/** Etiqueta visible del plan RoboTeam · para títulos y "tu plan: …". */
export const ROBOTEAM_TIER_LABEL: Record<RoboTeamTier, string> = {
  basic: 'RoboTeam Basic',
  pro: 'RoboTeam Pro',
};

/** Rango numérico del tier · pro > basic. Para comparar acceso a bloques. */
const ROBOTEAM_TIER_RANK: Record<RoboTeamTier, number> = {
  basic: 1,
  pro: 2,
};

/**
 * ¿El bloque de nivel `blockLevel` queda BLOQUEADO para un usuario de tier
 * `userTier`? Bloqueado = el tier del usuario es inferior al nivel del bloque
 * (p. ej. usuario Basic ante un bloque `pro`). Un usuario de tier igual o
 * superior tiene acceso. La página usa esto para decidir el render gris+candado;
 * el bloque locked NUNCA renderiza datos reales (upsell, no filtración).
 */
export function isBlockLocked(userTier: RoboTeamTier, blockLevel: RoboTeamTier): boolean {
  return ROBOTEAM_TIER_RANK[userTier] < ROBOTEAM_TIER_RANK[blockLevel];
}

/** URL pública de la página de ventas de RoboTeam (fuera del campus). */
export const ROBOTEAM_SALES_URL = 'https://roboteam.robotea.es/';

/** Ruta interna del área de miembros RoboTeam (item de menú del campus). */
export const ROBOTEAM_MEMBER_PATH = '/roboteam';

/**
 * ¿El alumno YA tiene RoboTeam? Acepta cualquier entitlement RoboTeam: el slug
 * genérico 'roboteam' o los slugs por plan ('roboteam-basic' / 'roboteam-pro').
 * Mientras la sync GHL→entitlement no siembre ninguno, devuelve false → los
 * banners llevan a la venta (fail-closed, deny-by-default).
 */
export function hasRoboTeam(
  enrolledSlugs: ReadonlySet<string> | readonly string[],
): boolean {
  const has = (slug: string): boolean =>
    enrolledSlugs instanceof Set
      ? enrolledSlugs.has(slug)
      : (enrolledSlugs as readonly string[]).includes(slug);
  return has('roboteam') || has(ROBOTEAM_BASIC_SLUG) || has(ROBOTEAM_PRO_SLUG);
}

/**
 * Destino del CTA/banner de RoboTeam según el acceso del alumno:
 *   - tiene RoboTeam  → área de miembros interna (`/roboteam`, router Link).
 *   - no tiene        → página de ventas pública (URL externa, `<a>`).
 * `internal` indica cuál usar al renderizar (Link vs `<a target="_blank">`).
 */
export function roboTeamCtaHref(
  enrolledSlugs: ReadonlySet<string> | readonly string[],
): { href: string; internal: boolean } {
  return hasRoboTeam(enrolledSlugs)
    ? { href: ROBOTEAM_MEMBER_PATH, internal: true }
    : { href: ROBOTEAM_SALES_URL, internal: false };
}
